import React, { useRef, useState, useEffect, useCallback, createContext, useContext } from 'react';
import Draggable from 'react-draggable';
import { Card, Typography, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { ChevronLeft, ChevronRight, Settings as SettingsIcon, Check as CheckIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import WidgetPanel from './_dashboard/WidgetPanel';
// Remove widgets imports that cause circular dependencies - they'll be imported dynamically
import { keyframes } from '@mui/system';
import CreateIcon from '@mui/icons-material/Create';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useDashboardData } from '../contexts/DashboardDataContext';
import { widgetService } from '../services/widgetService';

// Create Dashboard context for component-specific functionality only
export const DashboardContext = createContext();

// Custom hook for widgets to access dashboard local state and functions
export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
};

// Define widgets array with lazy loading to avoid circular dependencies
const widgets = widgetService.getWidgets();

// Create refs for draggable widgets
const nodeRefs = {};
for (let i = 1; i <= 11; i++) {
    nodeRefs[i] = React.createRef();
}

const ZONES = [
    { id: 0, name: "Dashboard" },  // Changed from "Main Dashboard" to "Dashboard"
];

// Define CACHE_TIMEOUT constant for periodic refresh checks
const CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

const Dashboard = () => {
    const widgetPanelRef = useRef(null);
    const [zIndexes, setZIndexes] = useState({});
    const [topZIndex, setTopZIndex] = useState(1);
    const [positions, setPositions] = useState({});
    const [loading, setLoading] = useState(true);
    const [isWidgetPanelOpen, setIsWidgetPanelOpen] = useState(false);
    const [openConfirmReset, setOpenConfirmReset] = useState(false);
    const [hasNoWidgets, setHasNoWidgets] = useState(true);
    const [hoverTimeout, setHoverTimeout] = useState(null);
    const [currentZone, setCurrentZone] = useState(0);
    const [slideDirection, setSlideDirection] = useState("slideUp");
    const [key, setKey] = useState(0); // Add this new state for forcing animation reset
    const [lastManualChange, setLastManualChange] = useState(Date.now()); // Move this up here
    const ZONE_HEIGHT = window.innerHeight;
    const TOTAL_ZONES = 3; // Antall soner vi ønsker å ha
    const [isEditingZone, setIsEditingZone] = useState(null);
    const [newZoneName, setNewZoneName] = useState('');
    const [zones, setZones] = useState([
        { id: 0, name: "Dashboard" }  // Only keep the default Dashboard zone
    ]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [originalZones, setOriginalZones] = useState([]);
    const [showTitle, setShowTitle] = useState(true);
    const [activeWidgets, setLocalActiveWidgets] = useState([]); // Add local state for widgets

    // Get last zone and scheduler from context - move this up before any usage
    const { lastZone, setLastZone, scheduler, rotation, setSchedulerEnabled, setSchedulerRules, setSchedulerRotation, isDashboardActive, fetchSchedulerConfig, setDashboardActive, setActiveWidgets: setContextActiveWidgets } = useDashboardData();

    const pulseAnimation = keyframes`
        0% {
            box-shadow: 0 0 0 0 rgba(251, 255, 0, 0.7);
        }
        70% {
            box-shadow: 0 0 0 20px rgba(255, 255, 255, 0);
        }
        100% {
            box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
        }
    `;

    const slideUpKeyframes = keyframes`
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
    `;
    const slideDownKeyframes = keyframes`
        from { transform: translateY(-100%); }
        to { transform: translateY(0); }
    `;

    const fadeInKeyframes = keyframes`
        from { opacity: 0; }
        to { opacity: 1; }
    `;
    
    const bringToFront = (widgetId) => {
        setZIndexes((prevZIndexes) => ({
            ...prevZIndexes,
            [widgetId]: topZIndex + 1
        }));
        setTopZIndex(topZIndex + 1);
    };

    const getCookie = (name) => {
        return document.cookie
            .split('; ')
            .find(row => row.startsWith(`${name}=`))
            ?.split('=')[1] || null;
    };

	const saveDashboardConfig = async (payload = null) => {
		const token = getCookie('token');
		if (!token) {
			console.error("Token not found in cookies.");
			return;
		}
	
		const apiUrl = `${process.env.REACT_APP_API_URL}/dashboard/save`;
		
		try {
			 // Format widget data to ensure each widget has a zone property
            const formattedWidgets = activeWidgets.map(widget => ({
                ...widget,
                // Ensure each widget has a single zone (use first zone from zones array)
                zone: widget.zones?.[0] || currentZone,
                // Keep zones array for internal use
                zones: widget.zones
            }));

			// Bruk enten gitt payload eller generer ny
			const configPayload = payload || {
				dashboard_config: {
					positions: Object.entries(positions).map(([key, pos]) => {
						const [wId, zId] = key.split('_').map(Number);
						return {
							id: wId,
							x: pos.x,
							y: pos.y,
							zone: zId // Make sure zone is always included
						};
					}),
					activeWidgets: formattedWidgets,
					zones: zones
				}
			};
	
			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
				body: JSON.stringify(configPayload)
			});
	
			if (!response.ok) {
				throw new Error(`Failed to save dashboard config: ${response.status}`);
			}
		} catch (error) {
			console.error("Failed to save dashboard config:", error);
		}
	};

    // Update loadDashboardConfig to set activeWidgets in DashboardDataContext
    const loadDashboardConfig = async () => {
        const token = getCookie('token');
        if (!token) {
            console.error("Token not found in cookies.");
            return;
        }

        const apiUrl = `${process.env.REACT_APP_API_URL}/dashboard/load`;

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 404) {
                setHasNoWidgets(true);
                setLocalActiveWidgets([]); // Ensure we set empty array
                setContextActiveWidgets([]); // Ensure we set empty array
            } else if (response.ok) {
                const result = await response.json();

                if (result.dashboard_config) {
                    // Konverter positions fra array til objekt med nye composite keys
                    const positionsObject = result.dashboard_config.positions.reduce((acc, pos) => {
                        const positionKey = `${pos.id}_${pos.zone}`;
                        acc[positionKey] = { 
                            x: pos.x, 
                            y: pos.y,
                            zone: pos.zone 
                        };
                        return acc;
                    }, {});
                    setPositions(positionsObject);

                    // Ensure each widget has a zones array
                    const processedWidgets = result.dashboard_config.activeWidgets.map(widget => ({
                        ...widget,
                        zones: Array.isArray(widget.zones) ? widget.zones : [widget.zone || 0]
                    }));

                    setLocalActiveWidgets(processedWidgets);
                    setContextActiveWidgets(processedWidgets);
                    setHasNoWidgets(processedWidgets.length === 0);
                    
                    // Load saved zones if they exist, ensuring Dashboard zone is always present
                    if (result.dashboard_config.zones && Array.isArray(result.dashboard_config.zones)) {
                        const hasMainDashboard = result.dashboard_config.zones.some(zone => zone.id === 0);
                        const updatedZones = hasMainDashboard 
                            ? result.dashboard_config.zones 
                            : [{ id: 0, name: "Dashboard" }, ...result.dashboard_config.zones];
                        
                        setZones(updatedZones);
                        console.log('📊 Loaded zones:', updatedZones);
                    }
                }
            }
        } catch (error) {
            console.error("Error loading dashboard configuration:", error);
            setHasNoWidgets(true);
            setLocalActiveWidgets([]); // Ensure we set empty array on error
            setContextActiveWidgets([]); // Ensure we set empty array on error
        } finally {
            setLoading(false);
        }
    };


    const handleToggleWidget = (widget, isChecked) => {
        setLocalActiveWidgets((prevWidgets) => {
            let updatedWidgets;
            const existingWidget = prevWidgets.find(w => w.id === widget.id);

            if (isChecked) {
                if (existingWidget) {
                    // Widget exists, add current zone to its zones array if not already present
                    if (!existingWidget.zones.includes(currentZone)) {
                        updatedWidgets = prevWidgets.map(w => 
                            w.id === widget.id 
                                ? { ...w, zones: [...w.zones, currentZone].sort() }
                                : w
                        );
                    } else {
                        return prevWidgets; // No change needed
                    }
                } else {
                    // Create new widget with current zone
                    const newWidget = {
                        ...widget,
                        zones: [currentZone]
                    };
                    updatedWidgets = [...prevWidgets, newWidget];

                    // Initialize position for the widget in this zone
                    setPositions(prev => ({
                        ...prev,
                        [`${widget.id}_${currentZone}`]: {
                            x: 0,
                            y: 0,
                            zone: currentZone
                        }
                    }));
                }
            } else {
                if (existingWidget) {
                    // Remove current zone from widget's zones
                    const updatedZones = existingWidget.zones.filter(zone => zone !== currentZone);
                    if (updatedZones.length === 0) {
                        // If no zones left, remove widget entirely
                        updatedWidgets = prevWidgets.filter(w => w.id !== widget.id);
                    } else {
                        // Update zones for the widget
                        updatedWidgets = prevWidgets.map(w => 
                            w.id === widget.id 
                                ? { ...w, zones: updatedZones }
                                : w
                        );
                    }
                } else {
                    return prevWidgets; // No change needed
                }
            }

            // Update context state
            setContextActiveWidgets(updatedWidgets);
            return updatedWidgets;
        });
    };

    const handleResetLayout = async () => {
        try {
            // First, clear all active widgets
            setLocalActiveWidgets([]);
            setContextActiveWidgets([]);
            
            const token = getCookie('token');
            if (!token) {
                console.error("Token not found in cookies.");
                return;
            }

            const response = await fetch(`${process.env.REACT_APP_STATUS_API_URL}/dashboard/reset`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                // Reset local state
                setPositions({});
                await loadDashboardConfig();
            } else {
                console.error('Failed to reset layout');
            }
        } catch (error) {
            console.error('Error resetting layout:', error);
        }
    };

    // Get last zone from context - remove scheduler

    // Move handleZoneChange definition to the top, before any useEffect hooks
    const handleZoneChange = useCallback((zoneIndex) => {
        // Check if the zone exists in our zones array
        const zoneExists = zones.some(zone => zone.id === zoneIndex);
        
        if (zoneIndex >= 0 && zoneExists) {
            // Get zone names for logging
            const fromZone = zones.find(z => z.id === currentZone)?.name || 'Unknown';
            const toZone = zones.find(z => z.id === zoneIndex)?.name || 'Unknown';
            
            console.log(
                `👆 %c[ZONE] MANUAL: "${fromZone}" → "${toZone}"`,
                'color: #2196f3; font-weight: bold'
            );
            
            // Lagre nåværende sone for å kunne oppdatere positions i bakgrunnen
            const previousZone = currentZone;
            
            // Utfør sonebytte umiddelbart
            setShowTitle(false);
            setLastManualChange(Date.now());
            
            const currentIndex = zones.findIndex(z => z.id === currentZone);
            const targetIndex = zones.findIndex(z => z.id === zoneIndex);
            
            // Only set slide direction if we have valid indices
            if (currentIndex !== -1 && targetIndex !== -1) {
                setSlideDirection(targetIndex > currentIndex ? "slideUp" : "slideDown");
            }
            
            setCurrentZone(zoneIndex);
            setLastZone(zoneIndex);
            setKey(prev => prev + 1);
            
            // Vis tittel etter kort forsinkelse
            setTimeout(() => {
                setShowTitle(true);
            }, 50); // Redusert fra 100ms til 50ms
            
            // Lagre posisjoner i bakgrunnen uten å vente
            const positionsArray = Object.entries(positions).map(([key, pos]) => {
                const [wId, zId] = key.split('_').map(Number);
                return {
                    id: wId,
                    x: pos.x,
                    y: pos.y,
                    zone: zId
                };
            });

            saveDashboardConfig({
                dashboard_config: {
                    positions: positionsArray,
                    activeWidgets: activeWidgets.map(widget => ({
                        ...widget,
                        zones: Array.isArray(widget.zones) ? widget.zones : [widget.zone || 0]
                    })),
                    zones: zones
                }
            }).catch(err => {
                console.error("Failed to save positions during zone change:", err);
            });
        }
    }, [zones, currentZone, setLastZone, positions, activeWidgets, saveDashboardConfig]);

    // Implementer debounced saveDashboardConfig
    const debounceSave = useCallback((fn, delay) => {
        let timeoutId;
        return (...args) => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                fn(...args);
            }, delay);
        };
    }, []);

    // Debounced versjon av saveDashboardConfig for hyppige oppdateringer
    const debouncedSave = useCallback(
        debounceSave(saveDashboardConfig, 1000),
        [saveDashboardConfig]
    );

    // Oppdater handleDragStop for å bruke debounced save
    const handleDragStop = (widgetId, e, data) => {
        const positionKey = `${widgetId}_${currentZone}`;
        const newPosition = { 
            x: data.x, 
            y: data.y,
            zone: currentZone
        };
        
        setPositions(prevPositions => {
            const updatedPositions = {
                ...prevPositions,
                [positionKey]: newPosition
            };
            
            // Bruk debounced save for drag operasjoner
            const positionsArray = Object.entries(updatedPositions).map(([key, pos]) => {
                const [wId, zId] = key.split('_').map(Number);
                return {
                    id: wId,
                    x: pos.x,
                    y: pos.y,
                    zone: zId
                };
            });

            debouncedSave({
                dashboard_config: {
                    positions: positionsArray,
                    activeWidgets: activeWidgets,
                    zones: zones
                }
            });
            
            return updatedPositions;
        });
    };

    // Optimalisert handleWheel funksjon for raskere respons
    const handleWheel = useCallback((event) => {
        // Unngå å behandle hjulhendelser under animasjon eller for raskt etter hverandre
        const now = Date.now();
        if (now - lastManualChange < 300) return; // Hindre for hyppige sonebytter
        
        // Finn gjeldende indeks i zones-arrayet
        const currentIndex = zones.findIndex(z => z.id === currentZone);
        
        if (event.deltaY > 0 && currentIndex < zones.length - 1) {
            // Scroll ned - flytt til neste sone i arrayet
            const nextZone = zones[currentIndex + 1];
            handleZoneChange(nextZone.id);
        } else if (event.deltaY < 0 && currentIndex > 0) {
            // Scroll opp - flytt til forrige sone i arrayet
            const prevZone = zones[currentIndex - 1];
            handleZoneChange(prevZone.id);
        }
    }, [zones, currentZone, handleZoneChange, lastManualChange]); // Add missing dependencies

    // Update useEffect for initial zone setting
    useEffect(() => {
        loadDashboardConfig().then(() => {
            setCurrentZone(lastZone);
        });
    }, [lastZone]);

    useEffect(() => {
        if (!loading) {
            // Kun lagre ved endringer i widgets eller soner, ikke posisjoner
            const saveTimeout = setTimeout(() => {
                if (activeWidgets.length > 0 || zones.length > 0) {
                    saveDashboardConfig();
                }
            }, 500);
            
            return () => clearTimeout(saveTimeout);
        }
    }, [activeWidgets, zones]); // Fjernet positions fra dependencies

    useEffect(() => {
        setHasNoWidgets(activeWidgets.length === 0);
    }, [activeWidgets]);

    const toggleWidgetPanel = () => {
        setIsWidgetPanelOpen((prev) => !prev);
    };

    const handleMouseEnter = () => {
        const timeout = setTimeout(() => {
            if (!isWidgetPanelOpen) {
                setIsWidgetPanelOpen(true);
            }
        }, 300); // 300ms delay før panelet åpnes
        setHoverTimeout(timeout);
    };

    const handleMouseLeave = () => {
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (widgetPanelRef.current && !widgetPanelRef.current.contains(event.target)) {
                setIsWidgetPanelOpen(false);
            }
        };

        if (isWidgetPanelOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isWidgetPanelOpen]);

    // Fix the moveToZone function to handle animations based on array position
    // const moveToZone = (zoneIndex) => {
    //     if (zoneIndex >= 0 && zoneIndex < zones.length) {
    //         setShowTitle(false);
            
    //         // Find current index in zones array
    //         const currentIndex = zones.findIndex(z => z.id === currentZone);
    //         const targetIndex = zones.findIndex(z => z.id === zoneIndex);
            
    //         // Determine slide direction based on array position, not zone ID
    //         setSlideDirection(targetIndex > currentIndex ? "slideUp" : "slideDown");
    //         setCurrentZone(zoneIndex);
    //         setLastZone(zoneIndex); // Save last zone
    //         setKey(prev => prev + 1);
            
    //         setTimeout(() => {
    //             setShowTitle(true);
    //         }, 100);
    //     }
    // };

    // Add state for loaded components
    const [loadedComponents, setLoadedComponents] = useState({});

    // Single renderWidgetComponent implementation with caching
    const renderWidgetComponent = (widgetId) => {
        // Return cached component if available
        if (loadedComponents[widgetId]) {
            return loadedComponents[widgetId];
        }

        // Find widget config for showing widget name in loading/error states
        const widgetConfig = widgets.find(w => w.id === widgetId);
        const widgetName = widgetConfig ? widgetConfig.name : `Widget ${widgetId}`;

        // Create a loading placeholder component
        const LoadingPlaceholder = () => (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>Loading {widgetName}...</div>
            </div>
        );

        // Set initial loading placeholder immediately
        if (!loadedComponents[widgetId]) {
            // Important: Set the initial placeholder synchronously
            setLoadedComponents(prev => ({
                ...prev,
                [widgetId]: LoadingPlaceholder
            }));
        }

        // Load component asynchronously
        widgetService.loadWidget(widgetId)
            .then(Component => {
                console.log(`Widget ${widgetName} loaded successfully`);
                // Wrap the component to ensure proper context access
                const WrappedComponent = (props) => {
                    return <Component {...props} />;
                };
                
                setLoadedComponents(prev => ({
                    ...prev,
                    [widgetId]: WrappedComponent
                }));
            })
            .catch(error => {
                console.error(`Failed to load widget ${widgetName}:`, error);
                const ErrorComponent = () => (
                    <div style={{ padding: '10px', backgroundColor: '#ffdddd', color: '#aa0000' }}>
                        <h4>Failed to load {widgetName}</h4>
                        <p>{String(error)}</p>
                    </div>
                );
                setLoadedComponents(prev => ({
                    ...prev,
                    [widgetId]: ErrorComponent
                }));
            });

        // Return current component (either placeholder or loaded component)
        return loadedComponents[widgetId] || LoadingPlaceholder;
    };

    // Add effect to manage dashboard active state
    useEffect(() => {
        setDashboardActive(true);
        
        return () => {
            setDashboardActive(false);
        };
    }, [setDashboardActive]);

    // Add rotation interval ref
    const rotationIntervalRef = useRef(null);
    // Additional refs for scheduler checks
    const schedulerTimeoutRef = useRef(null);
    const schedulerIntervalRef = useRef(null);

// Add a ref for currentZone right after state declarations
const currentZoneRef = useRef(currentZone);
useEffect(() => {
    currentZoneRef.current = currentZone;
}, [currentZone]);

// Define rotateToNextZone to always use the latest currentZone and compute nextIndex modulo zones.length
const rotateToNextZone = useCallback(() => {
    if (!zones || zones.length <= 1) {
        console.log('🔄 %c[ROTATION] SKIPPED: Not enough zones to rotate', 'color: #888');
        return;
    }
    const currentIndex = zones.findIndex(z => z.id === currentZoneRef.current);
    // Use modulo on the array index, not the zone ID
    const nextIndex = (currentIndex + 1) % zones.length;
    const nextZoneId = zones[nextIndex].id;
    
    console.log(
        `🔄 %c[ROTATION] AUTO: Zone ${currentIndex} (${zones[currentIndex]?.name}) → Zone ${nextIndex} (${zones[nextIndex]?.name})`,
        'color: #4caf50; font-weight: bold'
    );
    
    handleZoneChange(nextZoneId);
}, [zones, handleZoneChange]);

    // IMPORTANT: Remove ALL other rotation-related useEffects and keep ONLY this one
    // This includes the ones around lines ~722-842 and any other conflicting rotation setup
    useEffect(() => {
        // Clear any existing interval
        if (rotationIntervalRef.current) {
            clearInterval(rotationIntervalRef.current);
            rotationIntervalRef.current = null;
        }

        // Log the complete rotation state to help debug
        console.log('[ROTATION] Current state:', JSON.stringify(rotation));

        // Get rotation settings from context with explicit boolean comparison
        const rotationEnabled = rotation?.enabled === true;
        const rotationInterval = Math.max(60, rotation?.interval || 300);
        
        // Log rotation status for debugging
        if (rotationEnabled) {
            console.log(
                `⏱️ %c[ROTATION] ENABLED: Auto-rotation with ${rotationInterval} seconds interval`,
                'color: #4caf50; font-weight: bold'
            );
        } else {
            console.log(
                `⏹️ %c[ROTATION] DISABLED: rotation not enabled`, 
                'color: #888'
            );
            return; // Don't set up rotation if not enabled
        }
        
        // Only setup rotation if explicitly enabled AND we have multiple zones
        if (rotationEnabled && !loading && zones.length > 1) {
            // Use the configured interval for both initial delay and subsequent rotations
            const intervalMs = rotationInterval * 1000;
            
            // Initial rotation after a delay based on the configured interval
            const initialTimeout = setTimeout(() => {
                console.log(`🔄 %c[ROTATION] FIRST: Executing initial rotation after ${rotationInterval} seconds`, 'color: #9c27b0; font-weight: bold');
                rotateToNextZone();
                
                // Setup the interval for subsequent rotations
                rotationIntervalRef.current = setInterval(() => {
                    // Only rotate if page is visible
                    if (document.visibilityState === 'visible') {
                        console.log('🔄 %c[ROTATION] TICK: Executing scheduled rotation', 'color: #4caf50');
                        rotateToNextZone();
                    } else {
                        console.log('⏸️ %c[ROTATION] PAUSE: Page not visible, skipping rotation', 'color: #ff9800');
                    }
                }, intervalMs);
                
                console.log(`⏱️ %c[ROTATION] ACTIVE: Running every ${Math.floor(intervalMs/1000)} seconds`, 'color: #4caf50');
            }, intervalMs);  // Changed from 5000 to intervalMs

            return () => {
                clearTimeout(initialTimeout);
                if (rotationIntervalRef.current) {
                    clearInterval(rotationIntervalRef.current);
                    rotationIntervalRef.current = null;
                    console.log('⏹️ %c[ROTATION] CLEANUP: Rotation interval cleared', 'color: #f44336');
                }
            };
        } else {
            // Log why rotation is not active
            const reasons = [];
            if (!rotationEnabled) reasons.push('rotation not enabled');
            if (loading) reasons.push('dashboard still loading');
            if (zones.length <= 1) reasons.push('not enough zones for rotation');
            
            console.log(
                `⏹️ %c[ROTATION] INACTIVE: ${reasons.join(', ')}`, 
                'color: #888'
            );
        }
    }, [
        rotation, // Use rotation directly instead of accessing properties
        rotateToNextZone,
        loading,
        zones.length
    ]);

    // KEEP ONLY this specific event listener effect for custom rotation events
    useEffect(() => {
        const handleZoneRotation = () => {
            if (!zones || zones.length <= 1) return;
            rotateToNextZone();
        };

        window.addEventListener('zoneRotation', handleZoneRotation);
        
        return () => {
            window.removeEventListener('zoneRotation', handleZoneRotation);
        };
    }, [zones, rotateToNextZone]);

    const dashboardContextValue = {
        currentZone,
        zones,
        moveToZone: handleZoneChange
    };

    // CONSOLIDATE all dashboard active effects into just this one
    useEffect(() => {
        console.log('[Dashboard] Component mounted, setting dashboard active');
        setDashboardActive(true);
        
        // Removed reference to fetchDashboardData (now handled in DashboardDataContext)
        // Previously:
        // setTimeout(() => {
        //     console.log('[Dashboard] Triggering initial data fetch');
        //     fetchDashboardData(true);
        // }, 50);
        
        return () => {
            console.log('[Dashboard] Component unmounting, setting dashboard inactive');
            setDashboardActive(false);
        };
    }, [setDashboardActive]);

    const handleAddZone = () => {
        const newId = Math.max(...zones.map(z => z.id)) + 1;
        // Opprett en midlertidig sone med tom tekst
        const tempZone = { id: newId, name: '' };
        
        // Update zones state with new zone
        setZones(prevZones => {
            const updatedZones = [...prevZones, tempZone];
            return updatedZones;
        });
        
        // Prepare for editing
        setNewZoneName('');
        setIsEditingZone(newId);
        
        // Enable edit mode if not already enabled
        if (!isEditMode) {
            setIsEditMode(true);
        }
        
        // Navigate to the new zone
        setTimeout(() => {
            // We use setTimeout to ensure the zones state has been updated
            handleZoneChange(newId);
        }, 50);
    };

    const handleDeleteZone = (zoneId) => {
        if (zoneId === 0) return; // Protect Main Dashboard
        setZones(zones.filter(z => z.id !== zoneId));
        setLocalActiveWidgets(prevWidgets => 
            prevWidgets.map(widget => ({
                ...widget,
                zones: widget.zones.filter(z => z !== zoneId)
            })).filter(widget => widget.zones.length > 0)
        );
        setContextActiveWidgets(prevWidgets => 
            prevWidgets.map(widget => ({
                ...widget,
                zones: widget.zones.filter(z => z !== zoneId)
            })).filter(widget => widget.zones.length > 0)
        );
        if (currentZone === zoneId) {
            setCurrentZone(0);
        }
    };

    const handleZoneNameChange = (zoneId, newName) => {
        if (newName.trim()) {
            // Sjekk om dette er en ny sone (ikke har navn ennå)
            const isNewZone = !zones.find(z => z.id === zoneId)?.name;
            
            // Oppdater sonen i state
            setZones(zones.map(zone => 
                zone.id === zoneId ? { ...zone, name: newName.trim() } : zone
            ));

            // Hvis dette er en ny sone, lagre config umiddelbart
            if (isNewZone) {
                saveDashboardConfig();
            }
        } else {
            // Hvis navnet er tomt, fjern sonen
            setZones(zones.filter(z => z.id !== zoneId));
        }
        setIsEditingZone(null);
    };

    // Legg til handleWheel for sonebytte via scroll
/*     const handleWheel = (event) => {
        // Unngå å behandle hjulhendelser under animasjon eller for raskt etter hverandre
        const now = Date.now();
        if (now - lastManualChange < 300) return; // Hindre for hyppige sonebytter
        
        // Finn gjeldende indeks i zones-arrayet
        const currentIndex = zones.findIndex(z => z.id === currentZone);
        
        if (event.deltaY > 0 && currentIndex < zones.length - 1) {
            // Scroll ned - flytt til neste sone i arrayet
            const nextZone = zones[currentIndex + 1];
            handleZoneChange(nextZone.id);
        } else if (currentIndex > 0) {
            // Scroll opp - flytt til forrige sone i arrayet
            const prevZone = zones[currentIndex - 1];
            handleZoneChange(prevZone.id);
        }
    };
 */
    // Function to render a widget component - load it dynamically
/*     const renderWidgetComponent = async (widgetId) => {
        try {
            const Component = await widgetService.loadWidget(widgetId);
            return Component;
        } catch (error) {
            console.error(`Error rendering widget ${widgetId}:`, error);
            return () => <p>Error loading widget</p>;
        }
    }; */

    const handleMoveZone = (zoneId, direction) => {
        const currentIndex = zones.findIndex(z => z.id === zoneId);
        if (direction === 'up' && currentIndex > 1) { // Ikke flytt Dashboard (index 0)
            const newZones = [...zones];
            [newZones[currentIndex], newZones[currentIndex - 1]] = [newZones[currentIndex - 1], newZones[currentIndex]];
            setZones(newZones);
            saveDashboardConfig(); // Legg til umiddelbar lagring
        } else if (direction === 'down' && currentIndex < zones.length - 1) {
            const newZones = [...zones];
            [newZones[currentIndex], newZones[currentIndex + 1]] = [newZones[currentIndex + 1], newZones[currentIndex]];
            setZones(newZones);
            saveDashboardConfig(); // Legg til umiddelbar lagring
        }
    };

    // Modifiser handleZoneClick for å håndtere klikk på soner
    const handleZoneClick = (e, zoneId) => {
        // Hvis vi klikker på kontroll-knappene, ikke gjør noe
        if (e.target.closest('.zone-controls')) {
            return;
        }
        handleZoneChange(zoneId);
    };

    // Modifiser handleEditModeToggle for å bare håndtere lagring
    const handleEditModeToggle = () => {
        if (!isEditMode) {
            setIsEditMode(true);
        } else {
            setIsEditMode(false);
            saveDashboardConfig();
        }
    };


    // Add scheduler check effect
    useEffect(() => {
        if (!scheduler.enabled || !Array.isArray(scheduler.rules)) return;

        const checkSchedule = () => {
            const now = new Date();
            const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
            
            scheduler.rules.forEach(rule => {
                if (rule.time === currentTime && currentZone !== rule.zoneId) {
                    console.log(`Scheduler: Moving to zone ${rule.zoneName} (${rule.zoneId}) at ${currentTime}`);
                    handleZoneChange(rule.zoneId);
                }
            });
        };

        // Set up interval for future checks (every minute exactly on the minute)
        const now = new Date();
        const secondsToNextMinute = 60 - now.getSeconds();
        
        // First check after waiting until the start of the next minute
        const initialTimeoutId = setTimeout(() => {
            console.log('[Dashboard] Checking schedule at minute boundary');
            checkSchedule();
            
            // Then set up the regular interval exactly on minute boundaries
            const scheduleIntervalId = setInterval(checkSchedule, 60000);
            
            // Store the interval ID for cleanup
            schedulerIntervalRef.current = scheduleIntervalId;
        }, secondsToNextMinute * 1000);
        
        // Store the timeout ID for cleanup
        schedulerTimeoutRef.current = initialTimeoutId;
        
        return () => {
            if (schedulerTimeoutRef.current) {
                clearTimeout(schedulerTimeoutRef.current);
            }
            if (schedulerIntervalRef.current) {
                clearInterval(schedulerIntervalRef.current);
            }
        };
    }, [scheduler, currentZone, handleZoneChange]); // Only necessary dependencies

    // Modify the Dashboard active effect - fixed syntax
    useEffect(() => {
        console.log('[Dashboard] Component mounted, setting dashboard active');
        setDashboardActive(true);
        
        // Don't set up additional scheduler polling here - rely on DashboardDataContext
        // for centralized scheduler checks
        
        return () => {
            console.log('[Dashboard] Component unmounting, setting dashboard inactive');
            setDashboardActive(false);
        };
    }, [setDashboardActive]);

    // Add state to track last manual change
    const MANUAL_CHANGE_DELAY = 60000; // 1 minute delay after manual change


    // Enhanced polling effect with better logging
    // useEffect(() => {
    //     if (!isDashboardActive) {
    //         console.log('[Dashboard] Dashboard is not active, skipping scheduler polling');
    //         return;
    //     }
    // 
    //     console.log('[Dashboard] Dashboard is active, setting up scheduler data polling');
    // }, [isDashboardActive]);

    // Add an effect to log when dashboard becomes active/inactive
    useEffect(() => {
        console.log(`[Dashboard] isDashboardActive changed to: ${isDashboardActive}`);
    }, [isDashboardActive]);

    // REMOVE DUPLICATE active effect
    // useEffect(() => {
    //     console.log('[Dashboard] Component mounted, setting dashboard active');
    //     setDashboardActive(true);
    //     
    //     // Don't set up additional scheduler polling here - rely on DashboardDataContext
    //     // for centralized scheduler checks
    //     
    //     return () => {
    //         console.log('[Dashboard] Component unmounting, setting dashboard inactive');
    //     setDashboardActive(false);
    //     };
    // }, [setDashboardActive]);
    
    // Remove the fetchSchedulerData function and its polling to eliminate duplicate requests
    // The scheduler checking is now centralized in DashboardDataContext

    // Update effect to handle auto-rotation - separated from scheduler state
    useEffect(() => {
        const handleZoneRotation = () => {
            if (!zones || zones.length <= 1) return;
            rotateToNextZone();
        };

        // Listen for rotation events
        window.addEventListener('zoneRotation', handleZoneRotation);
        
        return () => {
            window.removeEventListener('zoneRotation', handleZoneRotation);
        };
    }, [zones, rotateToNextZone]);

    // Remove the duplicate rotation setup from before
    useEffect(() => {
        console.log('[Dashboard] Component mounted, setting dashboard active');
        setDashboardActive(true);
        
        return () => {
            console.log('[Dashboard] Component unmounting, setting dashboard inactive');
        setDashboardActive(false);
        };
    }, [setDashboardActive]);

    // Remove periodic scheduler config check since it's handled in DashboardDataContext
    useEffect(() => {
        if (!isDashboardActive) return;
    }, [isDashboardActive]);

    // Single definition of handleLoadScheduler
/*     const handleLoadScheduler = async () => {
        await fetchSchedulerConfig(); // Use the context's fetchSchedulerConfig instead of checkSchedulerChanges
    };
 */
    return (
        <DashboardContext.Provider value={dashboardContextValue}>
            <Box
                id="dashboard-page"
                onWheel={handleWheel} // Ny onWheel-hendelse for sonebytte
                onClick={() => isEditMode && handleEditModeToggle()} // Endre til å bruke samme funksjon som Done-knappen
                sx={{
                    width: '100%',
                    height: '100vh',
                    position: 'relative',
                    overflow: 'hidden !important', // Add !important to ensure it's applied
                    backgroundImage:
                        'linear-gradient(0deg, transparent 19px, #2b2b2b 20px), linear-gradient(90deg, transparent 19px, #2b2b2b 20px)',
                    backgroundSize: '20px 20px',
                }}
            >
                <Typography
                    variant="h3" 
                    gutterBottom
                    sx={{
                        opacity: 0,
                        position: 'relative',
                        zIndex: 2,
                        animation: showTitle ? `${fadeInKeyframes} 0.3s ease-in forwards` : 'none',
                        animationDelay: '0.5s'
                    }}
                >
                    {zones.find(zone => zone.id === currentZone)?.name || "Dashboard"}
                </Typography>

                {/* Widget Panel Toggle Button */}
                <Box
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    sx={{
                        position: 'absolute',
                        top: '20px',
                        right: 0,
                        width: '60px',
                        height: '40px',
                        zIndex: 1199,
                    }}
                >
                    <IconButton 
                        onClick={toggleWidgetPanel}
                        sx={{
                            position: 'absolute',
                            right: isWidgetPanelOpen ? '320px' : '0px',
                            transition: 'right 0.3s ease',
                            backgroundColor: '#333',
                            color: '#fff',
                            borderRadius: '0 5px 5px 0',
                            width: '40px',
                            height: '40px',
                            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
                            '&:hover': { backgroundColor: '#444' },
                            animation: hasNoWidgets && !isWidgetPanelOpen ? 
                                `${pulseAnimation} 2s infinite` : 'none',
                        }}
                    >
                        {isWidgetPanelOpen ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                </Box>

                {/* Widget Panel */}
                <WidgetPanel
                    ref={widgetPanelRef}
                    isOpen={isWidgetPanelOpen}
                    onToggleWidget={handleToggleWidget}
                    activeWidgets={activeWidgets}
                    onResetLayout={() => setOpenConfirmReset(true)}
                    currentZone={currentZone}
                />

                {/* Reset Dialog */}
                <Dialog open={openConfirmReset} onClose={() => setOpenConfirmReset(false)}>
                    <DialogTitle>Reset Layout</DialogTitle>
                    <DialogContent>
                        Are you sure you want to reset all widget positions? This will deactivate all widgets.
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenConfirmReset(false)}>Cancel</Button>
                        <Button 
                            onClick={() => {
                                handleResetLayout();
                                setOpenConfirmReset(false);
                            }}
                            color="error"
                        >
                            Reset
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Widgets Container */}
                <Box key={key} // Add key to force remount and animation reset
                    sx={{ 
                        position: 'relative',
                        height: '100%',
                        animation: `${slideDirection === 'slideUp' ? slideUpKeyframes : slideDownKeyframes} 0.5s forwards`
                    }}
                >
                    <Box sx={{ position: 'relative', top: 50, left: 50 }} 
                        onClick={(e) => e.stopPropagation()} // Hindre at klikk på widgets avbryter redigering
                    >
                        {!loading && activeWidgets && Array.isArray(activeWidgets) &&
                        activeWidgets
                            .filter(widget => {
                                // Ensure widget zones are properly handled
                                return Array.isArray(widget.zones) 
                                    ? widget.zones.includes(currentZone)
                                    : widget.zone === currentZone;
                            })
                            .map((widget) => {
                                const WidgetComponent = loadedComponents[widget.id] || renderWidgetComponent(widget.id);
                                if (!WidgetComponent) return null;

                                const positionKey = `${widget.id}_${currentZone}`;
                                const position = positions[positionKey] || { 
                                    x: 0, 
                                    y: 0,
                                    zone: currentZone 
                                };

                                return (
                                    <Draggable
                                        key={positionKey}
                                        nodeRef={nodeRefs[widget.id]}
                                        grid={[20, 20]}
                                        onStop={(e, data) => handleDragStop(widget.id, e, data)}
                                        onStart={() => bringToFront(widget.id)}
                                        position={{ x: position.x, y: position.y }}
                                    >
                                        <div 
                                            ref={nodeRefs[widget.id]} 
                                            style={{ 
                                                position: 'absolute', 
                                                zIndex: zIndexes[widget.id] || 1 
                                            }}
                                        >
                                            <Card sx={{ padding: 2, bgcolor: '#333', color: '#fff' }}>
                                                <WidgetComponent key={`widget-${widget.id}-${currentZone}`} />
                                            </Card>
                                        </div>
                                    </Draggable>
                                );
                            })}
                    </Box>
                </Box>

                {/* Zone Navigation */}
                <Box sx={{
                    position: 'fixed',
                    right: '20px',
                    bottom: '52px', // Increased from 20px to position above the status bar
                    zIndex: 1400, // Increased from 1200 to be higher than status bar's 1300
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    alignItems: 'flex-end'
                }}
                    onClick={(e) => e.stopPropagation()} // Hindre at klikk på sone-menyen avbryter redigering
                >
                    {zones.map((zone) => (
                        <Box
                            key={zone.id}
                            onClick={(e) => handleZoneClick(e, zone.id)} // Endre onClick håndtering
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: isEditMode ? '240px' : '36px',
                                height: '36px',
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                padding: '4px 12px',
                                borderRadius: '18px',
                                transition: 'all 0.3s ease',
                                opacity: currentZone === zone.id ? 0.9 : 0.6,
                                overflow: 'hidden',
                                '&:hover': isEditMode ? {
                                    opacity: 0.9,
                                    backgroundColor: 'rgba(0,0,0,0.4)'
                                } : {
                                    opacity: 0.9,
                                    backgroundColor: 'rgba(0,0,0,0.4)',
                                    width: 'auto', // Endret fra fast bredde til auto
                                    minWidth: '36px', // Minimum bredde når kollapset
                                    maxWidth: '200px' // Maksimal bredde når ekspandert
                                }
                            }}
                        >
                            <Box
                                onClick={() => !isEditMode && handleZoneChange(zone.id)}
                                sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: !isEditMode ? 'pointer' : 'default',
                                    width: '100%'
                                }}
                            >
                                <Box sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexGrow: 1,
                                    flexShrink: 1,
                                    minWidth: 0, // Viktig for at text-overflow skal fungere
                                    justifyContent: isEditMode ? 'flex-start' : 'flex-start',
                                }}>
                                    <Box
                                        sx={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: currentZone === zone.id ? '#1677ff' : 'rgba(255, 255, 255, 0.3)',
                                            flexShrink: 0,
                                            transition: 'all 0.3s ease',
                                            boxShadow: currentZone === zone.id ? '0 0 10px #1677ff' : 'none'
                                        }}
                                    />
                                    {isEditingZone === zone.id ? (
                                        <input
                                            type="text"
                                            value={newZoneName}
                                            onChange={(e) => setNewZoneName(e.target.value)}
                                            onBlur={() => {
                                                handleZoneNameChange(zone.id, newZoneName);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && newZoneName.trim()) {
                                                    handleZoneNameChange(zone.id, newZoneName); // Fix typo: newName -> newZoneName
                                                } else if (e.key === 'Escape') {
                                                    // If ESC is pressed, remove the zone if it's new
                                                    if (!zones.find(z => z.id === zone.id)?.name) {
                                                        setZones(zones.filter(z => z.id !== zone.id));
                                                    }
                                                    setIsEditingZone(null);
                                                }
                                            }}
                                            autoFocus
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                outline: 'none',
                                                color: '#fff',
                                                width: '120px',
                                                marginLeft: '8px',
                                                fontSize: '0.875rem',
                                                fontFamily: 'inherit'
                                            }}
                                        />
                                    ) : (
                                        <Typography 
                                            className="zone-name"
                                            variant="body2" 
                                            sx={{
                                                color: '#fff',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                opacity: isEditMode ? 1 : 0,
                                                width: isEditMode ? 'auto' : 0,
                                                marginLeft: '8px',
                                                maxWidth: isEditMode ? '100px' : '140px',
                                                transition: 'all 0.3s ease',
                                                '.MuiBox-root:hover &': {
                                                    opacity: 1,
                                                    width: 'auto'
                                                }
                                            }}
                                        >
                                            {zone.name}
                                        </Typography>
                                    )}
                                </Box>
                                {isEditMode && zone.id !== 0 && (
                                    <Box 
                                        className="zone-controls" // Legg til className for å identifisere kontroll-knapper
                                        sx={{
                                            display: 'flex',
                                            gap: 0.5,
                                            flexShrink: 0, // Hindrer at kontrollene krymper
                                            '.MuiIconButton-root': {
                                            }
                                        }}
                                    >
                                        <IconButton 
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveZone(zone.id, 'up');
                                            }}
                                        >
                                            <ArrowUpward fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveZone(zone.id, 'down');
                                            }}
                                        >
                                            <ArrowDownward fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsEditingZone(zone.id);
                                                setNewZoneName(zone.name);
                                            }}
                                        >
                                            <CreateIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteZone(zone.id);
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                    {/* Edit Mode Toggle Button */}
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditModeToggle();
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            opacity: 0.6,
                            cursor: 'pointer',
                            backgroundColor: isEditMode ? 'rgba(22, 119, 255, 0.3)' : 'rgba(0,0,0,0.3)',
                            padding: '4px',
                            borderRadius: '18px',
                            width: isEditMode ? '100px' : '36px', // Fast bredde i edit mode
                            height: '36px',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': { 
                                opacity: 0.9,
                                backgroundColor: isEditMode ? 'rgba(22, 119, 255, 0.4)' : 'rgba(0,0,0,0.4)',
                                width: '100px',
                                padding: '4px 12px',
                                justifyContent: 'flex-start'
                            }
                        }}
                    >
                        {isEditMode ? (
                            <>
                                <CheckIcon sx={{ color: '#fff' }} />
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: '#fff',
                                        opacity: 1, // Alltid synlig
                                        marginLeft: '4px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Done
                                </Typography>
                            </>
                        ) : (
                            <>
                                <CreateIcon sx={{ 
                                    color: '#fff',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': {
                                        transform: 'scale(1.1)'
                                    }
                                }} />
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: '#fff',
                                        width: 0,
                                        opacity: 0,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                        '.MuiBox-root:hover &': {
                                            width: 'auto',
                                            opacity: 1,
                                            marginLeft: '4px'
                                        }
                                    }}
                                >
                                    Edit
                                </Typography>
                            </>
                        )}
                    </Box>
                    {/* Add Zone button */}
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAddZone();
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            opacity: 0.6,
                            cursor: 'pointer',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            padding: '4px',
                            borderRadius: '18px',
                            width: '36px',
                            height: '36px',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': { 
                                opacity: 0.9,
                                backgroundColor: 'rgba(0,0,0,0.4)',
                                width: '120px', // Redusert fra 180px
                                padding: '4px 12px',
                                justifyContent: 'flex-start'
                            }
                        }}
                    >
                        <AddIcon sx={{ 
                            color: '#fff',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                                transform: 'scale(1.1)'
                            }
                        }} />
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: '#fff',
                                width: 0,
                                opacity: 0,
                                overflow: 'hidden',
                                transition: 'all 0.3s ease',
                                textAlign: 'center',
                                whiteSpace: 'nowrap', // Legg til denne linjen
                                '.MuiBox-root:hover &': {
                                    width: 'auto',
                                    opacity: 1,
                                    marginLeft: '4px'
                                }
                            }}
                        >
                            Add Zone
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </DashboardContext.Provider>
    );
};

export default Dashboard;